import React, { useEffect, useState, useRef } from 'react';
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('superadmin');
  const [showAlert, setShowAlert] = useState(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [userRole, setUserRole] = useState('');
  const topRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    companyId: ""
  });

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.role || 'user');
        console.log('User role:', decoded.role);
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchDepartments(selectedCompanyId);
    }
  }, [selectedCompanyId, userRole]);

  const fetchCompanies = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("No access token found. Please log in.");
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      setUserRole(decoded.role || 'user');

      if (decoded.role === 'superadmin') {
        // Superadmin can see all companies
        try {
          const response = await axios.get(
            `https://greenyellow-nightingale-241896.hostingersite.com/api/companies`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCompanies(response.data || []);
          console.log('Superadmin companies:', response.data);
        } catch (err) {
          console.error("Error fetching companies for superadmin:", err);
          setCompanies([]);
        }
      } else if (decoded.role === 'company') {
        // Company admin - fetch company directly
        const companyId = decoded.companyId || decoded.id;
        if (!companyId) {
          setError("No companyId found in token.");
          setLoading(false);
          return;
        }

        try {
          const response = await axios.get(
            `https://greenyellow-nightingale-241896.hostingersite.com/api/companies/${companyId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCompanies([response.data]);
          setSelectedCompanyId(companyId);
        } catch (err) {
          console.error("Error fetching company:", err);
          setError("Failed to fetch company data.");
        }
      } else {
        // Employee / Manager - get company ID from token's company field
        const companyId = decoded.company || decoded.companyId;
        const employeeId = decoded.id || decoded._id;
        if (!companyId) {
          setError("No company found in token.");
          setLoading(false);
          return;
        }

        // Try to get company name from employee profile (which now returns company object)
        try {
          const profileResponse = await axios.get(
            `https://greenyellow-nightingale-241896.hostingersite.com/api/employees/${employeeId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const companyData = profileResponse.data?.company;
          if (companyData && typeof companyData === 'object') {
            setCompanies([{ _id: companyId, businessName: companyData.businessName }]);
          } else {
            setCompanies([{ _id: companyId, businessName: 'My Company' }]);
          }
        } catch (err) {
          setCompanies([{ _id: companyId, businessName: 'My Company' }]);
        }
        setSelectedCompanyId(companyId);
      }
    } catch (err) {
      console.error("Invalid token:", err);
      setError("Invalid token. Please log in again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (companyId) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("No access token found.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let url;
      let config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      if (userRole === 'superadmin' && companyId === 'superadmin') {
        // Fetch superadmin departments (no company association)
        url = `https://greenyellow-nightingale-241896.hostingersite.com/api/departments/superadmin/all`;
        console.log('Fetching superadmin departments');
      } else {
        // Fetch company departments
        url = `https://greenyellow-nightingale-241896.hostingersite.com/api/departments/company/${companyId}`;
        console.log('Fetching company departments for:', companyId);
      }

      const response = await axios.get(url, config);
      console.log('Departments response:', response.data);
      setDepartments(response.data || []);
    } catch (err) {
      console.error("Error fetching departments:", err);
      console.error("Error details:", err.response?.data);

      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: value
    }));
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    if (!formData.name.trim()) {
      setShowAlert("Please enter a department name.");
      return;
    }

    setSubmitting(true);

    try {
      let url, payload;
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      if (userRole === 'superadmin' && selectedCompanyId === 'superadmin') {
        // Create superadmin department
        url = `https://greenyellow-nightingale-241896.hostingersite.com/api/departments/superadmin`;
        payload = { name: formData.name.trim() };
      } else {
        // Create company department
        const companyId = userRole === 'superadmin' ? selectedCompanyId : selectedCompanyId;
        if (!companyId || companyId === 'superadmin') {
          setShowAlert("Please select a company first.");
          return;
        }
        url = `https://greenyellow-nightingale-241896.hostingersite.com/api/departments/company/${companyId}`;
        payload = { name: formData.name.trim() };
      }

      console.log('Creating department:', url, payload);
      const response = await axios.post(url, payload, config);

      if (response.status === 201) {
        setShowSuccessAlert("Department created successfully!");
        fetchDepartments(selectedCompanyId);
        resetForm();
      } else {
        setShowAlert(response.data?.message || "Error creating department");
      }
    } catch (err) {
      console.error("Error creating department:", err);
      const errorMessage = err.response?.data?.message || "Error creating department. Please try again.";
      setShowAlert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditDepartment = (dept) => {
    setIsEditing(true);
    setEditingDeptId(dept._id);
    setFormData({
      name: dept.name || "",
      companyId: dept.company || selectedCompanyId
    });
    setShowForm(true);
    setScrollTrigger(prev => prev + 1);
  };

  useEffect(() => {
    if (showForm) {
      requestAnimationFrame(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [showForm, scrollTrigger]);

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    if (!formData.name.trim()) {
      setShowAlert("Please enter a department name.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.put(
        `https://greenyellow-nightingale-241896.hostingersite.com/api/departments/${editingDeptId}`,
        {
          name: formData.name.trim(),
          company: userRole === 'superadmin' && selectedCompanyId === 'superadmin' ? null : (formData.companyId || selectedCompanyId)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setShowSuccessAlert("Department updated successfully!");
      fetchDepartments(selectedCompanyId);
      resetForm();
    } catch (err) {
      console.error("Error updating department:", err);
      const errorMessage = err.response?.data?.message || "Error updating department";
      setShowAlert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteDepartment = (deptId, deptName) => {
    setConfirmAction({
      type: 'delete',
      message: `Are you sure you want to delete the department "${deptName}"?`,
      deptId: deptId
    });
  };

  // Execute the confirmed action
  const handleConfirmAction = async () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      setShowAlert('No access token found. Please log in.');
      setConfirmAction(null);
      return;
    }

    try {
      setSubmitting(true);

      if (confirmAction.type === 'delete') {
        const response = await axios.delete(
          `https://greenyellow-nightingale-241896.hostingersite.com/api/departments/${confirmAction.deptId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setShowSuccessAlert('Department deleted successfully!');
        fetchDepartments(selectedCompanyId);
      }

    } catch (error) {
      console.error("Error deleting department:", error);
      const errorMessage = error.response?.data?.message || "Error deleting department. Please try again.";
      setShowAlert(errorMessage);
    } finally {
      setSubmitting(false);
      setConfirmAction(null);
    }
  };

  // Close confirmation dialog
  const handleCloseAlert = () => {
    setConfirmAction(null);
  };

  // Close success alert
  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(null);
  };

  // Close error alert
  const handleCloseErrorAlert = () => {
    setShowAlert(null);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      companyId: selectedCompanyId || ""
    });
    setIsEditing(false);
    setEditingDeptId(null);
    setShowForm(false);
  };

  if (loading && !departments.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading departments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2 md:p-4">
      <div ref={topRef} className="max-w-full mx-auto"></div>
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="mb-4 md:mb-0">
            <h1 className="text-3xl font-bold text-gray-800">
              {userRole === 'superadmin' ? 'Organization Departments' : 'Department Management'}
            </h1>
            <p className="text-gray-600 mt-2">
              {userRole === 'superadmin'
                ? 'Manage organization-wide departments'
                : 'Manage departments for your company'}
            </p>
          </div>

          {/* Company Selector - Only show for superadmin */}
          {userRole === 'superadmin' && (
            <div className="mb-4 md:mb-0">
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="superadmin">Organization Departments</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.businessName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {userRole !== 'superadmin' && (
            <div className="mb-4 md:mb-0">
              <div className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm font-medium">
                {companies[0]?.businessName || 'Loading company...'}
              </div>
            </div>
          )}

          <button
            onClick={() => (showForm ? resetForm() : setShowForm(true))}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:-translate-y-1 shadow-md"
          >
            {showForm ? "Cancel" : "Add Department"}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex items-center">
              <div className="text-red-500 mr-3">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
            <h2 className="text-xl font-semibold mb-6 text-gray-800">
              {isEditing ? "Edit Department" : "Add New Department"}
            </h2>
            <form onSubmit={isEditing ? handleUpdateDepartment : handleAddDepartment}>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    required
                    disabled={submitting}
                    placeholder="Enter department name"
                  />
                </div>

                {isEditing && userRole === 'superadmin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Association
                    </label>
                    <select
                      name="companyId"
                      value={formData.companyId || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      disabled={submitting}
                    >
                      <option value="">Organization Department (No Company)</option>
                      {companies.map((company) => (
                        <option key={company._id} value={company._id}>
                          {company.businessName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={submitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {isEditing ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      isEditing ? "Update Department" : "Add Department"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Departments List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-800">
                {userRole === 'superadmin' && selectedCompanyId === 'superadmin'
                  ? 'Organization Departments'
                  : 'Departments'}
              </h2>
              <span className="text-sm text-gray-600">
                {departments.length} department{departments.length !== 1 ? 's' : ''} found
              </span>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {departments.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p className="text-lg mb-4">No departments found</p>
                <button
                  onClick={() => setShowForm(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                >
                  Add First Department
                </button>
              </div>
            ) : (
              departments.map((dept) => (
                <div key={dept._id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{dept.name}</h3>
                      {/* {dept.company ? (
                        <p className="text-sm text-gray-500">
                          Company: {dept.company.businessName || 'N/A'}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500">Organization Department</p>
                      )} */}
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleEditDepartment(dept)}
                        className="px-4 py-2 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDepartment(dept._id, dept.name)}
                        className="px-4 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Alert dialogs */}
      {confirmAction && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Confirmation</h2>
            <p className="text-gray-700 mb-6">{confirmAction.message}</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleConfirmAction}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Confirm'}
              </button>
              <button
                onClick={handleCloseAlert}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-xl hover:bg-gray-400 transition disabled:opacity-50"
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Success</h2>
            <p className="text-gray-700 mb-6">{showSuccessAlert}</p>
            <div className="flex justify-center">
              <button
                onClick={handleCloseSuccessAlert}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{showAlert}</p>
            <div className="flex justify-center">
              <button
                onClick={handleCloseErrorAlert}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Departments;

